import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import StreamControls from '../components/StreamControls';
import DecisionCard from '../components/DecisionCard';
import { clusterLane } from '../lib/clusterThemes';
import { visibleDecisions } from '../lib/quickWin';
import EditDecisionModal from '../components/EditDecisionModal';
import BulkActionBar from '../components/BulkActionBar';
import StaleDecisionsBanner from '../components/StaleDecisionsBanner';
import SignalSourcesTeaser from '../components/SignalSourcesTeaser';
import SignalSourcesPanel from '../components/SignalSourcesPanel';
import NotificationsPanel from '../components/NotificationsPanel';
import TrendCharts from '../components/TrendCharts';
import WelcomeBanner from '../components/WelcomeBanner';
import EmptyState from '../components/EmptyState';
import Footer from '../components/Footer';
import WorkspaceSwitcher from '../components/WorkspaceSwitcher';
import UsageMeter from '../components/UsageMeter';
import ProductTour from '../components/ProductTour';
import Logo from '../components/Logo';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Rocket, AlertCircle, XCircle, Home, Filter, DollarSign, Map, BookOpen, Upload, ArrowUpDown, Zap, Settings, Search } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppSidebar from '../components/AppSidebar';
import { API_URL } from '../lib/apiBase';

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const laneFromUrl = new URLSearchParams(location.search).get('lane');
  const filterFromUrl = new URLSearchParams(location.search).get('filter');
  const { currentWorkspaceId, refreshUsage } = useWorkspace();
  const [lanes, setLanes] = useState({
    build_next: [],
    fix_now: [],
    ignore: []
  });
  const [stats, setStats] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamSpeed, setStreamSpeed] = useState(1);
  const [loading, setLoading] = useState(true);
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [showTrends, setShowTrends] = useState(false);
  const [sortBy, setSortBy] = useState('urgency');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingDecision, setEditingDecision] = useState(null);
  const [deletingDecision, setDeletingDecision] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [groupedView, setGroupedView] = useState(false);
  const [expandedThemes, setExpandedThemes] = useState({});

  // Fetch decisions on mount
  useEffect(() => {
    if (currentWorkspaceId) {
      fetchDecisions();
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspaceId]);

  // Refetch when segment filter or sort changes
  useEffect(() => {
    if (!loading && currentWorkspaceId) {
      fetchDecisions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentFilter, sortBy]);

  const fetchDecisions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (segmentFilter !== 'all') params.append('segment', segmentFilter);
      if (sortBy) params.append('sort_by', sortBy);
      if (currentWorkspaceId) params.append('workspace_id', currentWorkspaceId);

      const response = await axios.get(`${API_URL}/api/decisions?${params.toString()}`);
      setLanes(response.data);
    } catch (error) {
      console.error('Error fetching decisions:', error);
      toast.error('Failed to load decisions');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stats`, {
        params: { workspace_id: currentWorkspaceId },
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleInjectScenario = async (scenario) => {
    try {
      toast.loading('Injecting scenario...');
      await axios.post(`${API_URL}/api/inject-scenario`, {
        scenario,
        workspace_id: currentWorkspaceId,
      });
      toast.dismiss();
      toast.success(`Scenario injected: ${scenario.replace('_', ' ')}`);
      
      await fetchDecisions();
      await fetchStats();
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to inject scenario');
      console.error(error);
    }
  };

  const handleClearLanes = async () => {
    try {
      toast.loading('Clearing lanes...');
      await axios.delete(`${API_URL}/api/decisions`, {
        params: { workspace_id: currentWorkspaceId },
      });
      toast.dismiss();
      toast.success('Lanes cleared');
      
      setLanes({
        build_next: [],
        fix_now: [],
        ignore: []
      });
      await fetchStats();
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to clear lanes');
      console.error(error);
    }
  };

  const handleUpdateStatus = async (signalId, newStatus) => {
    try {
      await axios.post(`${API_URL}/api/decisions/${signalId}/status`, {
        signal_id: signalId,
        status: newStatus
      });
      
      toast.success(`Marked as ${newStatus}`);
      
      if (newStatus === 'shipped') {
        toast.success('🎉 Feature shipped! Added to changelog.');
      }
      
      await fetchDecisions();
      await fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    }
  };

  const handleDeleteDecision = async () => {
    if (!deletingDecision) return;
    try {
      await axios.delete(`${API_URL}/api/decisions/${deletingDecision.signal_id}`);
      toast.success('Decision deleted');
      setDeletingDecision(null);
      await fetchDecisions();
      await fetchStats();
    } catch (error) {
      toast.error('Failed to delete decision');
      console.error(error);
    }
  };

  // Client-side search filter (case-insensitive across summary / why / signal text)
  const filterDecisionsByQuery = (list) => {
    if (!searchQuery.trim()) return list;
    const q = searchQuery.trim().toLowerCase();
    return list.filter(
      (d) =>
        (d.summary || '').toLowerCase().includes(q) ||
        (d.why || '').toLowerCase().includes(q) ||
        (d.suggested_next_step || '').toLowerCase().includes(q) ||
        (d.signal?.text || '').toLowerCase().includes(q)
    );
  };

  const toggleSelect = (signalId) => {
    setSelectedIds((s) =>
      s.includes(signalId) ? s.filter((id) => id !== signalId) : [...s, signalId]
    );
  };

  const clearSelection = () => setSelectedIds([]);

  const bulkAction = async (action, payload = {}) => {
    if (selectedIds.length === 0) return;
    try {
      const res = await axios.post(`${API_URL}/api/decisions/bulk`, {
        signal_ids: selectedIds,
        action,
        payload,
      });
      toast.success(`${res.data.affected} decision${res.data.affected === 1 ? '' : 's'} ${action.replace('_', ' ')}${action === 'delete' ? 'd' : ''}`);
      clearSelection();
      await fetchDecisions();
      await fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk action failed');
    }
  };

  const laneConfig = [
    {
      key: 'build_next',
      title: '🚀 Build Next',
      icon: Rocket,
      color: 'lane-build',
      description: 'High revenue opportunities',
      testId: 'lane-build-next'
    },
    {
      key: 'fix_now',
      title: '⚠️ Fix Now',
      icon: AlertCircle,
      color: 'lane-fix',
      description: 'Churn risks and blockers',
      testId: 'lane-fix-now'
    },
    {
      key: 'ignore',
      title: '❌ Ignore',
      icon: XCircle,
      color: 'lane-ignore',
      description: 'Low priority signals',
      testId: 'lane-ignore'
    }
  ];

  // Apply sidebar lane filter (from URL). If a specific lane is picked, hide the others.
  const visibleLaneConfig = laneFromUrl
    ? laneConfig.filter((l) => l.key === laneFromUrl)
    : laneConfig;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading decisions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex" data-testid="dashboard-shell">
      <AppSidebar />
      <div className="flex-1 min-w-0 flex flex-col">
      {/* Welcome Banner */}
      <WelcomeBanner />
      
      {/* Top bar (simplified — sidebar owns workspace switching & navigation) */}
      <div className="border-b bg-card sticky top-0 z-40">
        <div className="mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-orange-500/50 text-orange-600 uppercase tracking-wider font-semibold" data-testid="dashboard-alpha-badge">
                Alpha
              </Badge>
              <Badge variant="secondary" className="text-xs">Demo</Badge>

              {/* Revenue indicator */}
              {stats?.revenue && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Separator orientation="vertical" className="h-6" />
                  <DollarSign className="h-4 w-4 text-green-500" />
                  <span className="font-semibold text-foreground">
                    ${(stats.revenue.total_potential_mrr / 1000).toFixed(1)}k
                  </span>
                  <span>MRR potential</span>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2 flex-wrap">
              {/* Usage Meter (compact) */}
              <div data-tour="usage-meter">
                <UsageMeter variant="compact" />
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search decisions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-9 rounded-md border bg-background pl-8 pr-3 text-sm w-[220px] focus:outline-none focus:ring-2 focus:ring-primary/40"
                  data-testid="dashboard-search-input"
                />
              </div>

              {/* Sort By */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[130px]" data-testid="sort-by-select">
                  <ArrowUpDown className="h-3.5 w-3.5 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgency">By Urgency</SelectItem>
                  <SelectItem value="roi">By ROI</SelectItem>
                  <SelectItem value="revenue">By Revenue</SelectItem>
                  <SelectItem value="effort">By Effort</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Segment Filter */}
              <Select value={segmentFilter} onValueChange={setSegmentFilter}>
                <SelectTrigger className="w-[140px]" data-testid="segment-filter">
                  <Filter className="h-3.5 w-3.5 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Segments</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Add manually; automated Signal Sources live at /sources */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/import')}
                data-testid="nav-import-button"
                data-tour="import-button"
                title="Paste feedback for AI analysis, or connect Signal Sources."
              >
                <Upload className="h-3.5 w-3.5 mr-1.5" />
                Add manually
              </Button>
              
              {/* Quick Wins Link */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/app?filter=quick_wins')}
                data-testid="quick-wins-button"
              >
                <Zap className="h-3.5 w-3.5 mr-1.5" />
                Quick Wins
              </Button>
              
              {/* Toggle Trends */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTrends(!showTrends)}
                data-testid="toggle-trends-button"
              >
                {showTrends ? 'Hide Trends' : 'Show Trends'}
              </Button>

              <StreamControls
                isStreaming={isStreaming}
                streamSpeed={streamSpeed}
                onStartStop={() => setIsStreaming(!isStreaming)}
                onSpeedChange={setStreamSpeed}
                onInjectScenario={handleInjectScenario}
                onClear={handleClearLanes}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Trend Charts (collapsible) */}
      {showTrends && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="border-b bg-muted/30">
            <div className="container-wide mx-auto px-4 py-6">
              <TrendCharts />
            </div>
          </div>
        </motion.div>
      )}

      {/* Stale decisions banner (30+ days untouched) */}
      <StaleDecisionsBanner workspaceId={currentWorkspaceId} />

      {/* Signal Sources — Panel when a workspace is selected, else teaser */}
      <div className="container-wide mx-auto px-4 pt-4">
        {currentWorkspaceId ? (
          <SignalSourcesPanel
            workspaceId={currentWorkspaceId}
            onSignalsIngested={() => {
              // Refresh dashboard data & usage after a fetch surfaces new signals
              if (typeof refreshUsage === 'function') refreshUsage();
              // Trigger a lane reload by kicking the URL-less axios call
              fetchDecisions();
            }}
          />
        ) : (
          <SignalSourcesTeaser />
        )}
      </div>

      {/* Main content */}
      <div className="container-wide mx-auto px-4 py-6">
        <div className="flex justify-end items-center gap-2 mb-3"><span className="text-xs text-muted-foreground">Display</span><Button size="sm" variant={groupedView ? "default" : "outline"} onClick={() => setGroupedView(!groupedView)} aria-pressed={groupedView} data-testid="themes-toggle">{groupedView ? "Themes (grouped)" : "All signals"}</Button></div>
        <div className="dashboard-grid">
          {/* Lanes */}
          <div className="lanes-grid" data-tour="lanes">
            {visibleLaneConfig.map((lane) => {
              const rawDecisions = lanes[lane.key] || [];
              const decisions = filterDecisionsByQuery(
                visibleDecisions(rawDecisions, filterFromUrl)
              );
              
              return (
                <Card 
                  key={lane.key} 
                  className={`flex flex-col ${lane.color}`}
                  data-testid={lane.testId}
                >
                  {/* Lane header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b">
                    <div className="flex items-center gap-2">
                      <h2 className="text-sm font-semibold">{lane.title}</h2>
                      <Badge variant="secondary" className="text-xs">
                        {decisions.length}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Lane content */}
                  <ScrollArea className="h-[calc(100vh-220px)]">
                    <div className="p-3 space-y-3">
                      <AnimatePresence>
                        {decisions.length === 0 ? (
                          <EmptyState
                            lane={lane.key}
                            onSampleLoaded={() => { fetchDecisions(); fetchStats(); refreshUsage(); }}
                          />
                        ) : (
                          (groupedView ? clusterLane(decisions) : decisions.map(d => ({ id: d.signal_id, members: [d], count: 1, title: d.summary }))).map((theme, index) => (
                            <motion.div key={theme.id} initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{duration:0.18,delay:Math.min(index,10)*0.03}}>
                              {groupedView && theme.count > 1 && (
                                <button type="button" className="w-full text-left border rounded-md p-3 mb-2 bg-card hover:bg-muted" onClick={() => setExpandedThemes(prev => ({...prev,[theme.id]:!prev[theme.id]}))} aria-expanded={!!expandedThemes[theme.id]}>
                                  <div className="font-medium text-sm">{theme.title}</div>
                                  <div className="text-xs text-muted-foreground mt-1">{theme.reportCount} reports · Suggested grouping — review before acting · {expandedThemes[theme.id] ? 'Hide details' : 'Show details'}</div>
                                </button>
                              )}
                              {(theme.count === 1 || !groupedView || expandedThemes[theme.id]) && theme.members.map(decision => (
                                <div key={decision.signal_id} className="mb-2">
                                  <DecisionCard decision={decision} onUpdateStatus={handleUpdateStatus} onEdit={setEditingDecision} onDelete={setDeletingDecision} selectable selected={selectedIds.includes(decision.signal_id)} onToggleSelect={toggleSelect} />
                                </div>
                              ))}
                            </motion.div>
                          ))
                        )}
                      </AnimatePresence>
                    </div>
                  </ScrollArea>
                </Card>
              );
            })}
          </div>

          {/* Notifications panel */}
          <NotificationsPanel stats={stats} lanes={lanes} />
        </div>
      </div>

      {/* Footer */}
      <Footer variant="minimal" />

      {/* Edit decision drawer */}
      <EditDecisionModal
        open={!!editingDecision}
        onOpenChange={(open) => !open && setEditingDecision(null)}
        decision={editingDecision}
        onSaved={() => { fetchDecisions(); fetchStats(); }}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deletingDecision} onOpenChange={(open) => !open && setDeletingDecision(null)}>
        <AlertDialogContent data-testid="decision-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete decision?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove "{deletingDecision?.summary}" and its source signal permanently. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="decision-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDecision}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="decision-delete-confirm-button"
            >
              Delete decision
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Product tour overlay (auto-starts on first dashboard visit) */}
      <ProductTour />

      {/* Bulk action floating bar */}
      <BulkActionBar
        selectedIds={selectedIds}
        onClear={clearSelection}
        onBulkDelete={() => bulkAction('delete')}
        onBulkMove={(lane) => bulkAction('move_lane', { lane })}
        onBulkMarkShipped={() => bulkAction('mark_shipped')}
      />
      </div>
    </div>
  );
};

export default DashboardPage;
